import { Injectable } from '@angular/core';
import { MessageBroadcastingService as ApiMessageBroadcastingService } from '../api/services/message-broadcasting.service';
import { MessagingGroupModel } from '../api/models/messaging-group-model';
import { BehaviorSubject, lastValueFrom, Subject, Subscription } from 'rxjs';
import { EventsService } from '../events/events.service';
import { LocalNotificationsService } from '../core/services/local-notifications.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { NotificationModel } from '../api/models/notification-model';
import { ApiConfiguration } from '../api/api-configuration';
import {
  BroadcastThreadModel,
  BroadcastThreadViewModel,
  GridSortModel,
  MediaModel,
  MessageBroadcastThreadModel,
  QueueBroadcastMessageV2Model,
  SaveBroadcastThreadRequest, SaveBroadcastThreadResponse,
  SmsPhoneNumberModel,
} from '../api/models';

@Injectable()
export class MessageBroadcastingService {
  public getNumbersInProgress = true;
  public messagingNumbers: SmsPhoneNumberModel[] = [];
  public selectedMessagingNumber: SmsPhoneNumberModel = { phoneNumber: '' };
  public totalGroups = 0;
  public messagingGroups = new BehaviorSubject<BroadcastThreadViewModel[]>([]);
  public onNewMessageSubject = new BehaviorSubject<MessageBroadcastThreadModel>(null);
  public onWebSocketConnectSubject = new Subject<void>();
  public onNewNotificationsSubject = new BehaviorSubject<NotificationModel[]>([]);
  public notificationsSubscription: Subscription;

  constructor(
    private eventService: EventsService,
    private notificationsService: LocalNotificationsService,
    private httpClient: HttpClient,
    private apiConfig: ApiConfiguration,
    private apiMessageBroadcastingService: ApiMessageBroadcastingService,
  ) {
    this.eventService.subscribeEvents('broadcast', (event, data: MessageBroadcastThreadModel) => {
      this.onNewMessageSubject.next(data);
    });

    this.eventService.subscribeEvents('connect', (event, data: MessageBroadcastThreadModel) => {
      this.onWebSocketConnectSubject.next();
    });

    this.notificationsSubscription = this.notificationsService.latestNotifications.subscribe(data => {
      this.onNewNotificationsSubject.next(data);
      this.updatePhoneNumbersBadges(data);
    });
  }

  getMessagingNumber(): string {
    return this.selectedMessagingNumber ? this.selectedMessagingNumber.phoneNumber : '';
  }

  hasMessagingNumber() {
    return this.messagingNumbers.length > 0;
  }

  public async queryBroadcastThreads(options: {
    phoneNumber: string;
    skip?: number;
    limit?: number;
    searchText?: string;
    sort?: Array<GridSortModel>;
    id?: string;
    forceUpdate: boolean;
  }): Promise<BroadcastThreadViewModel[]> {
    console.log('update queryBroadcastGroups:', options.phoneNumber);

    if (!options.phoneNumber) {
      this.messagingGroups.next([]);
      return [];
    }

    const response = await lastValueFrom(
      this.apiMessageBroadcastingService.messageBroadcastingGetBroadcastThreads({
        phoneNumber: options.phoneNumber,
        body: {
          searchText: options.searchText,
          sort: options.sort,
          skip: options.skip || 0,
          limit: options.limit || 100,
          phoneNumber: options.phoneNumber,
          user: undefined,
        },
      }),
    );
    const allGroups = response.data;
    this.totalGroups = response.total;

    await this.updateGroupsBadges(allGroups, this.notificationsService.latestNotifications.getValue());

    return allGroups;
  }

  public async updateGroupsBadges(groups: MessagingGroupModel[], notifications: NotificationModel[]) {
    console.log('updateGroupsBadges: ' + notifications.length);
    let hasNotifications = false;
    for (const group of groups) {
      const notification = notifications.find(
        x => x.eventType === 'pro-sms' && x.tnTo === this.getMessagingNumber() && x.thread === group.threadKey,
      );

      if (notification) {
        hasNotifications = true;
        group.unread = notification.badge;
      } else {
        group.unread = 0;
      }
    }

    if (hasNotifications) {
      const messagingGroups = this.messagingGroups.getValue();
      this.messagingGroups.next(messagingGroups);
    }
  }

  public updatePhoneNumbersBadges(notifications: NotificationModel[]) {
    for (const number of this.messagingNumbers) {
      const phoneNotifications = notifications.filter(x => x.eventType === 'pro-sms' && x.tnTo === number.phoneNumber);
      let totalBadges = 0;
      for (const notification of phoneNotifications) {
        totalBadges += notification.badge;
      }
      number.badge = totalBadges;
    }
  }

  public async sendMessage(request: QueueBroadcastMessageV2Model, file?: File): Promise<boolean> {
    if (file) {
      const mediaUrl = await this.uploadMedia(file);
      if (mediaUrl) {
        request.media = [mediaUrl];
      }
    }

    const response = await lastValueFrom(
      this.apiMessageBroadcastingService.messageBroadcastingSendMessage({ body: request }),
    );

    console.log(response);

    return true;
  }

  public async uploadMedia(file: File): Promise<MediaModel> {
    const options: any = {
      method: 'post',
      url: this.apiConfig.rootUrl + `/api/advanced-messaging/upload-media`,
      params: new HttpParams(),
      headers: new HttpHeaders(),
    };
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return await lastValueFrom(this.httpClient.post<MediaModel>(options.url, formData, {}));
  }

  async getBroadcastThread(id: string): Promise<BroadcastThreadModel> {
    const results = await lastValueFrom(
      this.apiMessageBroadcastingService.messageBroadcastingGetBroadcastThread({
        id: id,
      }),
    );

    return results.data;
  }

  async saveBroadcastThread(broadcast: SaveBroadcastThreadRequest): Promise<SaveBroadcastThreadResponse> {
    if (broadcast) {
      const results = await lastValueFrom(this.apiMessageBroadcastingService
        .messageBroadcastingSaveBroadcastThread({
          body: broadcast,
        }));

      return results;
    } else {
      return null;
    }
  }

  public async deleteFullConversation(broadcastId: string): Promise<boolean> {
    if (!!broadcastId) {
      return await lastValueFrom(
        this.apiMessageBroadcastingService.messageBroadcastingDeleteBroadcastById({
          broadcastId: broadcastId,
        }),
      );
    }

    return false;
  }

  public async messagingPhoneNumbers(tn?: string, force?: boolean): Promise<SmsPhoneNumberModel[]> {
    console.log('messagingPhoneNumbers:', tn);
    this.getNumbersInProgress = true;
    if (!this.messagingNumbers || this.messagingNumbers.length === 0) {
      this.messagingNumbers = await lastValueFrom(
        this.apiMessageBroadcastingService.messageBroadcastingGetMyMessagingPhoneNumbers(),
      );
    }
    this.getNumbersInProgress = false;

    this.updatePhoneNumbersBadges(this.notificationsService.latestNotifications.getValue());

    if (force || (!this.getMessagingNumber() && this.messagingNumbers.length > 0)) {
      this.selectedMessagingNumber = this.messagingNumbers.find(x => x.phoneNumber === tn) || this.messagingNumbers[0];
    }

    return this.messagingNumbers;
  }

  public getContactsAndTagsObservable(searchText?: string) {
    return this.apiMessageBroadcastingService.messageBroadcastingLookupContactsAndTags({ q: searchText });
  }

  async getContactsCount(id: string) {
    return lastValueFrom(
      this.apiMessageBroadcastingService.messageBroadcastingGetBroadcastThreadContactsCount({ id: id }),
    );
  }
}
